import type { PageNode } from "@baze/schema";

/**
 * Tiny inline enhancer for the lessons section's search box and group filter chips.
 * The engine ships every topic row visible and the controls ([data-baze-lesson-ui]) `hidden`; for
 * each .baze-lessons section this script:
 * - reveals the controls;
 * - shows only rows whose group matches the pressed chip ([data-baze-lesson-filter], "" = all) and
 *   whose data-text contains every word typed in [data-baze-lesson-search];
 * - shows [data-baze-lesson-empty] when nothing matches;
 * - presses the matching chip when a group card ([data-baze-lesson-group]) is followed, and keeps the
 *   pressed chip in view;
 * - scrolls an opened topic to its top: the list it replaces collapses after the browser has scrolled.
 * Opening a topic is plain CSS :target; without this script it still works.
 */
export const LESSONS_SCRIPT =
  // Runs after load: React hydration restores server attributes, so un-hiding controls earlier would be undone.
  `(function(w,d){function s(){d.querySelectorAll(".baze-lessons").forEach(function(r){if(r.__baze)return;r.__baze=1;` +
  `var ui=r.querySelector("[data-baze-lesson-ui]");if(!ui)return;ui.hidden=false;` +
  `var q=r.querySelector("[data-baze-lesson-search]"),em=r.querySelector("[data-baze-lesson-empty]"),g="";` +
  `function run(){var ws=(q?q.value:"").toLowerCase().split(/\\s+/).filter(Boolean),k=0;` +
  `r.querySelectorAll("[data-baze-lesson-row]").forEach(function(e){var t=e.getAttribute("data-text")||"",ok=(g===""||e.getAttribute("data-group")===g)&&ws.every(function(x){return t.indexOf(x)>=0});e.hidden=!ok;if(ok)k++});` +
  `if(em)em.hidden=k>0}` +
  `function pick(v){g=v;r.querySelectorAll("[data-baze-lesson-filter]").forEach(function(b){var on=b.getAttribute("data-baze-lesson-filter")===g;b.setAttribute("aria-pressed",on?"true":"false");` +
  `if(on){var p=b.parentNode;p.scrollTo({left:b.offsetLeft-p.offsetLeft-(p.clientWidth-b.offsetWidth)/2,behavior:"smooth"})}});run()}` +
  `r.addEventListener("click",function(e){var b=e.target.closest&&e.target.closest("[data-baze-lesson-filter],[data-baze-lesson-group]");if(!b||!r.contains(b))return;` +
  `pick(b.hasAttribute("data-baze-lesson-filter")?b.getAttribute("data-baze-lesson-filter"):b.getAttribute("data-baze-lesson-group"))});` +
  `q&&q.addEventListener("input",run)});` +
  `if(!w.__bazeLessons){w.__bazeLessons=1;w.addEventListener("hashchange",top);top()}}` +
  `function top(){var h=location.hash.slice(1),e;try{e=h&&d.getElementById(decodeURIComponent(h))}catch(x){}` +
  `e&&e.hasAttribute("data-baze-lesson-detail")&&requestAnimationFrame(function(){e.scrollIntoView({block:"start"})})}` +
  `d.readyState=="complete"?s():w.addEventListener("load",s)})(window,document)`;

/** Pages with a visible lessons section get the script. */
export function needsLessonsScript(page: Pick<PageNode, "sections">): boolean {
  return page.sections.some((s) => !s.hidden && s.type === "lessons");
}
